import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { getImageUrl } from '../../utils/imageUtils';
import {
  HomeIcon,
  UserGroupIcon,
  UserIcon,
  UserPlusIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  BuildingOffice2Icon,
  BellIcon,
  Cog6ToothIcon,
  ClockIcon,
  HeartIcon,
  BeakerIcon,
  StarIcon,
  BuildingOfficeIcon,
  XMarkIcon,
  ChartBarIcon,
  CheckCircleIcon,
  SwatchIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  UserGroupIcon as UserGroupIconSolid,
  CalendarIcon as CalendarIconSolid,
  HeartIcon as HeartIconSolid,
  CurrencyDollarIcon as CurrencyDollarIconSolid,
} from '@heroicons/react/24/solid';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkflowCounts } from '../../hooks/useWorkflowCounts';
import SidebarGroup from '../sidebar/SidebarGroup';
import SidebarItem from '../sidebar/SidebarItem';

// Type definitions for navigation items
interface SubItem {
  name: string;
  href: string;
  description: string;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  activeIcon?: React.ComponentType<any>;
  permission: string; // Required permission in format "resource:action"
  description?: string;
  badge?: string;
  badgeColor?: string;
  priority?: boolean;
  subItems?: SubItem[];
}

interface SidebarProps {
  isCollapsed: boolean;
  onToggleSidebar: () => void;
  isMobileMenuOpen?: boolean;
  onCloseMobileMenu?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggleSidebar, isMobileMenuOpen, onCloseMobileMenu }) => {
  const { user, clinic, logout, hasPermission, userPermissions, isLoading, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Debug logs removed

  const handleLogoClick = () => {
    // Redirect based on user role
    if (hasRole('PHARMACIST')) {
      navigate('/pharmacy');
    } else if (hasRole('DOCTOR')) {
      navigate('/workflow/triage');
    } else if (hasRole('LAB_TECHNICIAN')) {
      navigate('/laboratory/queue');
    } else if (hasRole('RADIOLOGIST')) {
      navigate('/radiology/queue');
    } else if (hasRole('REPORTS')) {
      navigate('/reports/doctors');
    } else {
      navigate('/dashboard');
    }
  };


  // Use workflow counts hook
  const { counts: workflowCounts, loading: countsLoading } = useWorkflowCounts();

  // Sidebar state management
  const [activePopoverGroup, setActivePopoverGroup] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);

  const navigationGroups = [
    {
      id: 'main',
      name: 'Main Navigation',
      icon: HomeIconSolid,
      items: [
        {
          name: 'Dashboard',
          href: '/dashboard',
          icon: HomeIcon,
          activeIcon: HomeIconSolid,
          permission: 'dashboard:read',
          badge: '●',
          description: 'Overview & Analytics',
          priority: true,
          hideForRoles: ['PHARMACIST', 'ADMIN', 'RADIOLOGIST'] // Hide dashboard for pharmacists, administrators, and radiologists
        },
        {
          name: 'Patients',
          href: '/patients',
          icon: UserGroupIcon,
          activeIcon: UserGroupIconSolid,
          permission: 'patients:read',
          description: 'Patient Management',
          hideForRoles: ['DOCTOR', 'PHARMACIST', 'LAB_TECHNICIAN'] // Hide Patients menu for doctors, pharmacists, and lab technicians
        },
        {
          name: 'Doctors',
          href: '/doctors',
          icon: UserIcon,
          permission: 'doctors:read',
          description: 'Doctor Profiles'
        },
      ]
    },
    {
      id: 'appointments',
      name: 'Appointments & Schedule',
      icon: CalendarIconSolid,
      items: [
        {
          name: 'Scheduled Patients',
          href: '/workflow/scheduled',
          icon: CalendarIcon,
          activeIcon: CalendarIconSolid,
          permission: 'appointments:read',
          badge: countsLoading ? '...' : (workflowCounts.scheduledPatients > 0 ? workflowCounts.scheduledPatients.toString() : undefined),
          badgeColor: 'bg-gray-500',
          description: 'Check-in Queue'
        },
        {
          name: 'Appointments',
          href: '/appointments',
          icon: CalendarIcon,
          activeIcon: CalendarIconSolid,
          permission: 'appointments:read',
          description: 'Calendar View'
        },
      ]
    },
    {
      id: 'workflow',
      name: 'Patient Workflow',
      icon: HeartIconSolid,
      items: [
        {
          name: 'Waiting Patients',
          href: '/workflow/waiting',
          icon: ClockIcon,
          permission: 'waiting-patients:view',
          badge: countsLoading ? '...' : (workflowCounts.waitingPatients > 0 ? workflowCounts.waitingPatients.toString() : undefined),
          badgeColor: 'bg-yellow-500',
          description: 'Checked-in patients'
        },
        {
          name: 'Doctor Triage',
          href: '/workflow/triage',
          icon: UserIcon,
          permission: 'doctor-triage:view',
          badge: countsLoading ? '...' : (workflowCounts.triagePatients > 0 ? workflowCounts.triagePatients.toString() : undefined),
          badgeColor: 'bg-gray-500',
          description: 'Unassigned Patients & Self-Assignment'
        },
      ]
    },
    {
      id: 'pharmacy',
      name: 'Pharmacy Services',
      icon: BeakerIcon,
      permission: 'pharmacy:read',
      items: [
        {
          name: 'Pharmacy Dashboard',
          href: '/pharmacy',
          icon: DocumentTextIcon,
          permission: 'pharmacy:read',
          description: 'Pharmacy Overview'
        },
        {
          name: 'Pharmacy POS',
          href: '/pharmacy/pos',
          icon: ShoppingBagIcon,
          permission: 'pharmacy:read',
          description: 'Sales and Dispensing'
        },
        {
          name: 'Medicines & Inventory',
          href: '/pharmacy/medicines',
          icon: BeakerIcon,
          permission: 'pharmacy:read',
          description: 'Inventory Management'
        },
        {
          name: 'Prescription Reports',
          href: '/reports/pharmacy',
          icon: DocumentTextIcon,
          permission: 'pharmacy:read',
          description: 'Pharmacy Prescription Reports'
        },
        {
          name: 'Medicine Report',
          href: '/reports/medicine',
          icon: BeakerIcon,
          permission: 'pharmacy:read',
          description: 'Total qty and earnings per medicine'
        },
        {
          name: 'Stock Movement Report',
          href: '/reports/stock',
          icon: ChartBarIcon,
          permission: 'pharmacy:read',
          description: 'IN/OUT stock movements and low stock',
          showForRoles: ['ADMIN']
        }
      ]
    },
    {
      id: 'laboratory',
      name: 'Laboratory Services',
      icon: BeakerIcon,
      // href removed - only sub-items should be accessible, not direct /laboratory link
      permission: 'laboratory:read',
      items: [
        {
          name: 'Lab Queue',
          href: '/laboratory/queue',
          icon: ClipboardDocumentListIcon,
          permission: 'laboratory:read',
          description: 'View and manage lab test queue',
          showForRoles: ['ADMIN', 'LAB_TECHNICIAN']
        },
        {
          name: 'Test Types',
          href: '/laboratory/test-types',
          icon: DocumentTextIcon,
          permission: 'laboratory:read',
          description: 'Manage lab test types',
          showForRoles: ['ADMIN', 'LAB_TECHNICIAN']
        },
        {
          name: 'Category Tests',
          href: '/laboratory/category-tests',
          icon: BeakerIcon,
          permission: 'laboratory:read',
          description: 'Manage lab categories and tests',
          showForRoles: ['ADMIN', 'LAB_TECHNICIAN']
        },
        {
          name: 'Lab Order Approvals',
          href: '/lab/approvals',
          icon: CheckCircleIcon,
          permission: 'laboratory:read',
          description: 'Approve pending lab orders',
          showForRoles: ['RECEPTIONIST', 'ADMIN']
        }
      ]
    },
    {
      id: 'radiology',
      name: 'Radiology Services',
      icon: SwatchIcon,
      permission: 'laboratory:read',
      items: [
        {
          name: 'Radiology Queue',
          href: '/radiology/queue',
          icon: ClipboardDocumentListIcon,
          permission: 'laboratory:read',
          description: 'X-ray, Ultrasound, ECG orders',
          showForRoles: ['ADMIN', 'RADIOLOGIST']
        },
        {
          name: 'Pending Approvals',
          href: '/radiology/approvals',
          icon: CheckCircleIcon,
          permission: 'laboratory:read',
          description: 'Approve pending radiology orders',
          showForRoles: ['ADMIN']
        },
        {
          name: 'Categories & Pricing',
          href: '/radiology/categories',
          icon: BeakerIcon,
          permission: 'laboratory:read',
          description: 'Manage radiology categories and pricing',
          showForRoles: ['ADMIN', 'LAB_TECHNICIAN']
        },
      ]
    },
    {
      id: 'financial',
      name: 'Financial Management',
      icon: CurrencyDollarIconSolid,
      items: [
        {
          name: 'Claims Processing',
          href: '/claims',
          icon: ShieldCheckIcon,
          permission: 'claims:read',
          description: 'Process insurance claims'
        },
        {
          name: 'Verification',
          href: '/insurance-verification',
          icon: ShieldCheckIcon,
          permission: 'claims:read',
          description: 'Verify insurance policies'
        },
      ]
    },
    {
      id: 'hospital',
      name: 'Hospital Operations',
      icon: BuildingOffice2Icon,
      items: [
        {
          name: 'Admissions',
          href: '/admissions',
          icon: BuildingOffice2Icon,
          permission: 'admissions:read',
          description: 'Patient admissions'
        },
        {
          name: 'Ward & Beds',
          href: '/ward-management',
          icon: BuildingOffice2Icon,
          permission: 'admissions:read',
          description: 'Manage wards and beds'
        },
      ]
    },
    {
      id: 'reports',
      name: 'Reports',
      icon: ChartBarIcon,
      items: [
        {
          name: 'Doctors Report',
          href: '/reports/doctors',
          icon: UserIcon,
          permission: 'reports:read',
          description: 'Doctor SOAP Notes Report',
          showForRoles: ['ADMIN', 'DOCTOR', 'REPORTS']
        },
        {
          name: 'Laboratory Reports',
          href: '/reports/laboratory',
          icon: BeakerIcon,
          permission: 'reports:read',
          description: 'Comprehensive Laboratory Orders Report',
          showForRoles: ['RECEPTIONIST', 'ADMIN', 'LAB_TECHNICIAN', 'REPORTS']
        },
        {
          name: 'Radiology Reports',
          href: '/reports/radiology',
          icon: ChartBarIcon,
          permission: 'reports:read',
          description: 'Comprehensive Radiology Orders Report',
          showForRoles: ['ADMIN', 'LAB_TECHNICIAN', 'RADIOLOGIST', 'RECEPTIONIST', 'REPORTS']
        },
        {
          name: 'Pharmacy Reports',
          href: '/reports/pharmacy',
          icon: DocumentTextIcon,
          permission: 'pharmacy:read',
          description: 'Comprehensive Pharmacy Prescription Report',
          showForRoles: ['ADMIN', 'PHARMACIST', 'REPORTS']
        },
        {
          name: 'Medicine Report',
          href: '/reports/medicine',
          icon: BeakerIcon,
          permission: 'pharmacy:read',
          description: 'Total quantity and earnings per medicine',
          showForRoles: ['ADMIN', 'PHARMACIST', 'REPORTS']
        },
        {
          name: 'Stock Movement Report',
          href: '/reports/stock',
          icon: ChartBarIcon,
          permission: 'pharmacy:read',
          description: 'IN/OUT stock movements, top dispensed, low stock',
          showForRoles: ['ADMIN']
        },
        {
          name: 'Consultation Fee Reports',
          href: '/reports/consultation',
          icon: CurrencyDollarIcon,
          permission: 'reports:read',
          description: 'Comprehensive Consultation Fee Report',
          showForRoles: ['RECEPTIONIST', 'ADMIN', 'REPORTS']
        },
        {
          name: 'Admissions Cost Report',
          href: '/reports/admissions-cost',
          icon: CurrencyDollarIcon,
          permission: 'reports:read',
          description: 'Admission dates with lab, radiology, consultation, and pharmacy costs',
          showForRoles: ['RECEPTIONIST', 'ADMIN', 'REPORTS']
        }
      ]
    },
    {
      id: 'administration',
      name: 'System Administration',
      icon: StarIcon,
      items: [
        {
          name: 'System Settings',
          href: '/admin/settings',
          icon: Cog6ToothIcon,
          permission: 'admin:settings',
          description: 'System Configuration & Branding'
        },
        {
          name: 'Staff List',
          href: '/staff',
          icon: UserGroupIcon,
          permission: 'staff:read',
          description: 'View All Staff Members'
        },
        {
          name: 'Treatment Templates',
          href: '/admin/treatments',
          icon: ClipboardDocumentListIcon,
          permission: 'admin:treatments',
          description: 'Medical Treatment Protocols'
        },
        {
          name: 'Insurance Providers',
          href: '/admin/insurance',
          icon: ShieldCheckIcon,
          permission: 'insurance:manage',
          description: 'Insurance Companies Management'
        },
        {
          name: 'Insurance Agreements',
          href: '/admin/insurance-agreements',
          icon: DocumentTextIcon,
          permission: 'insurance:manage',
          description: 'Insurance Contracts & Terms',
          hideForRoles: ['ADMIN', 'DOCTOR', 'NURSE', 'PHARMACIST', 'LAB_TECHNICIAN', 'RECEPTIONIST', 'ACCOUNTANT', 'PATIENT']
        },
        {
          name: 'Audit Logs',
          href: '/admin/audit',
          icon: BellIcon,
          permission: 'audit:read',
          description: 'System Activity & Monitoring',
          showForRoles: ['ADMIN', 'REPORTS']
        },
      ]
    },
  ];

  // Get all group IDs for accordion behavior
  const allGroupIds = navigationGroups.map(g => g.id);

  const toggleGroup = (groupId: string) => {
    if (isCollapsed) {
      // If sidebar is collapsed, expand it and open the group
      onToggleSidebar();
      // Close all other groups, open only this one
      setCollapsedGroups(allGroupIds.filter((id) => id !== groupId));
    } else {
      // If sidebar is expanded, implement accordion behavior
      if (collapsedGroups.includes(groupId)) {
        // Opening this group - close all others
        setCollapsedGroups(allGroupIds.filter((id) => id !== groupId));
      } else {
        // Closing this group - just add it to collapsed groups
        setCollapsedGroups((prev) => [...prev, groupId]);
      }
    }
  };

  const isGroupCollapsed = (groupId: string) => collapsedGroups.includes(groupId);

  const isActiveRoute = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  // Helper function to check if user has access to a menu item
  const hasAccess = (item: any) => {
    // Check if item has showForRoles restriction (only show for specific roles)
    if (item.showForRoles && item.showForRoles.length > 0) {
      // If showForRoles is defined, only those roles can see it
      if (!user?.role || !item.showForRoles.includes(user.role)) {
        return false;
      }
    }

    // Administrators have access to all items
    if (user?.role === 'ADMIN') {
      // Still check excludeRoles and hideForRoles to respect those restrictions
      if (item.excludeRoles && item.excludeRoles.includes('ADMIN')) {
        return false;
      }
      if (item.hideForRoles && item.hideForRoles.includes('ADMIN')) {
        return false;
      }
      return true;
    }

    // Check permission requirement (permission is now required for all items)
    if (!item.permission) return false;

    // Check if user's role is excluded from this item
    if (item.excludeRoles && user?.role && item.excludeRoles.includes(user.role)) {
      return false;
    }

    // Check if user's role is in hideForRoles list
    if (item.hideForRoles && user?.role && item.hideForRoles.includes(user.role)) {
      return false;
    }

    // Use the permission string directly
    const hasRequiredPermission = hasPermission(item.permission);

    // Debug logs removed

    return hasRequiredPermission;
  };

  // Initialize collapsed groups - collapse all except the one with active route
  useEffect(() => {
    const findActiveGroup = () => {
      for (const group of navigationGroups) {
        // Check if group has direct href
        const groupHref = (group as any).href;
        if (groupHref) {
          const groupPermission = (group as any).permission;
          if (!groupPermission || hasAccess({ permission: groupPermission })) {
            if (location.pathname === groupHref || location.pathname.startsWith(groupHref + '/')) {
              return group.id;
            }
          }
        }
        // Check items
        for (const item of group.items) {
          if (hasAccess(item)) {
            // Check if main item is active
            if (location.pathname === item.href || location.pathname.startsWith(item.href + '/')) {
              return group.id;
            }
          }
        }
      }
      return null;
    };

    const activeGroup = findActiveGroup();
    const collapsedGroupIds = navigationGroups
      .map(g => g.id)
      .filter(id => id !== activeGroup);

    setCollapsedGroups(collapsedGroupIds);
  }, [location.pathname]);

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onCloseMobileMenu}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed lg:relative flex flex-col h-screen bg-white shadow-2xl border-r border-slate-200/60 transition-all duration-300 z-50 lg:z-auto ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 ${isCollapsed ? 'lg:w-20' : 'lg:w-56'
          } w-64`}
        style={{ overflow: 'visible' }}
        onMouseEnter={() => {
          // Optional: Auto-expand on hover for desktop only
          if (window.innerWidth >= 1024 && isCollapsed) {
            // onToggleSidebar();
          }
        }}
      >

        {/* Header */}
        <div className="h-16 sm:h-20 lg:h-20 flex-shrink-0 px-2 sm:px-3 lg:px-4 py-2 sm:py-3 lg:py-4 bg-white border-b border-slate-200/50">
          <div className="flex items-center justify-between h-full">
            {/* Logo and Clinic Info */}
            <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4 cursor-pointer active:scale-95 lg:hover:scale-105 transition-all duration-300" onClick={handleLogoClick}>
              <div className="flex-shrink-0">
                {clinic?.CLINIC_LOGO ? (
                  <div className="h-12 w-12 sm:h-14 sm:w-14 lg:h-16 lg:w-16 rounded-xl sm:rounded-2xl shadow-xl overflow-hidden active:scale-95 lg:hover:scale-105 transition-all duration-300 ring-2 ring-white/50">
                    <img
                      src={getImageUrl(clinic.CLINIC_LOGO) || ''}
                      alt="Clinic Logo"
                      className="w-full h-full object-contain bg-white"
                    />
                  </div>
                ) : (
                  <div className="h-10 w-10 sm:h-12 sm:w-12 lg:h-14 lg:w-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-gray-500 to-gray-600 shadow-xl flex items-center justify-center active:scale-95 lg:hover:scale-105 transition-all duration-300 ring-2 ring-gray-200">
                    <HeartIcon className="h-6 w-6 sm:h-7 sm:w-7 lg:h-9 lg:w-9 text-white" />
                  </div>
                )}
              </div>
              {!isCollapsed && (
                <div className="transition-opacity duration-300">
                  <h1 className="text-base sm:text-lg lg:text-xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent tracking-tight">
                    {clinic?.CLINIC_NAME || 'MedCare'}
                  </h1>
                  <p className="text-xs font-semibold text-gray-600">
                    {clinic?.CLINIC_TAGLINE || 'Clinic Management'}
                  </p>
                </div>
              )}
            </div>

            {/* Mobile Close Button */}
            {onCloseMobileMenu && (
              <button
                onClick={onCloseMobileMenu}
                className="lg:hidden p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors touch-manipulation"
                aria-label="Close mobile menu"
              >
                <XMarkIcon className="h-5 w-5 text-gray-600" />
              </button>
            )}
          </div>
        </div>

        {/* Scrollable Navigation Content */}
        <div className="flex-1 overflow-y-auto overflow-x-visible bg-white scrollbar-hide" style={{ overflowX: 'visible' }}>
          {isLoading ? (
            <div className="px-2 sm:px-3 lg:px-4 py-4 sm:py-5 lg:py-6 flex items-center justify-center">
              <div className="flex items-center space-x-2 text-slate-500">
                <div className="w-4 h-4 border-2 border-slate-300 border-t-gray-600 rounded-full animate-spin"></div>
                <span className="text-sm">Loading menu...</span>
              </div>
            </div>
          ) : (
            <nav className="px-1 sm:px-2 py-1 sm:py-2 space-y-0.5" style={{ overflow: 'visible' }}>
              {navigationGroups
                .filter((group) => {
                  // Check if group has direct href
                  const groupHref = (group as any).href;
                  const groupPermission = (group as any).permission;
                  if (groupHref) {
                    // Group has direct href - check permission
                    if (groupPermission && !hasAccess({ permission: groupPermission })) {
                      return false;
                    }
                  }

                  // Administrators can see all groups (except those explicitly hidden)
                  if (hasRole('ADMIN')) {
                    // Only hide groups if they have no accessible items and no direct href
                    if (groupHref) {
                      return true; // Group with direct href is accessible
                    }
                    return group.items.some((item) => hasAccess(item));
                  }

                  // Lab technicians: only show Laboratory Services
                  if (hasRole('LAB_TECHNICIAN') && group.id !== 'laboratory') {
                    return false;
                  }

                  // Radiologists: only show Radiology Services
                  if (hasRole('RADIOLOGIST') && group.id !== 'radiology') {
                    return false;
                  }

                  // Reports role: only Reports and Audit (under administration)
                  if (hasRole('REPORTS')) {
                    if (group.id === 'reports') return true;
                    if (group.id === 'administration') {
                      return group.items.some(
                        (item) => item.permission === 'audit:read' && hasAccess(item)
                      );
                    }
                    return false;
                  }

                  // Hide Pharmacy Services for doctors and receptionists
                  if (group.id === 'pharmacy' && (hasRole('DOCTOR') || hasRole('RECEPTIONIST'))) {
                    return false;
                  }
                  // Hide Radiology Services for receptionists
                  if (group.id === 'radiology' && hasRole('RECEPTIONIST')) {
                    return false;
                  }
                  // Hide Appointments & Schedule for doctors and lab technicians
                  if (group.id === 'appointments' && (hasRole('DOCTOR') || hasRole('LAB_TECHNICIAN'))) {
                    return false;
                  }
                  // Hide Hospital Operations for lab technicians
                  if (group.id === 'hospital' && hasRole('LAB_TECHNICIAN')) {
                    return false;
                  }
                  // Hide Hospital Operations (Admissions) for pharmacists
                  if (group.id === 'hospital' && hasRole('PHARMACIST')) {
                    return false;
                  }
                  // Only show Administration group if user has admin:read or audit:read
                  if (
                    group.id === 'administration' &&
                    !hasPermission('admin:read') &&
                    !hasPermission('audit:read')
                  ) {
                    return false;
                  }

                  // If group has direct href, it's accessible (permission already checked above)
                  if (groupHref) {
                    return true;
                  }

                  return group.items.some((item) => hasAccess(item));
                })
                .map((group) => {
                  // For laboratory group, hide direct href - only sub-items should be accessible
                  let groupHref = (group as any).href;
                  if (group.id === 'laboratory') {
                    groupHref = undefined; // Laboratory group should only show sub-items, not navigate to /laboratory
                  }

                  return (
                    <SidebarGroup
                      key={group.id}
                      id={group.id}
                      name={group.name}
                      icon={group.icon}
                      items={group.items}
                      href={groupHref}
                      permission={(group as any).permission}
                      isCollapsed={isCollapsed}
                      isGroupCollapsed={isGroupCollapsed(group.id)}
                      onToggleGroup={() => toggleGroup(group.id)}
                      hasAccess={hasAccess}
                      isPopoverActive={activePopoverGroup === group.id}
                      onPopoverChange={(isActive) => setActivePopoverGroup(isActive ? group.id : null)}
                    />
                  );
                })}
            </nav>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;